import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Button,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  IconButton,
  Divider,
  Paper,
  Link,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { googleMapsQueryUrl, formatJobDeliveryLine } from '../../utils/jobMapsLink';
import { buildCompleteJobFormData } from '../../utils/completeJobFormData';

const DriverDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [updatingLocation, setUpdatingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationSuccess, setLocationSuccess] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [hasVehicle, setHasVehicle] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [vehicleStatus, setVehicleStatus] = useState('');
  const [dieselQuantity, setDieselQuantity] = useState('');
  const [defQuantity, setDefQuantity] = useState('');
  const [updatingVehicle, setUpdatingVehicle] = useState(false);
  const [vehicleError, setVehicleError] = useState('');
  const [vehicleSuccess, setVehicleSuccess] = useState('');
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completionFormData, setCompletionFormData] = useState({
    diesel_delivered: '',
    def_delivered: '',
    completion_notes: '',
  });
  const [subJobs, setSubJobs] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [rejectingJob, setRejectingJob] = useState(false);
  const [rejectError, setRejectError] = useState('');
  const locationIntervalRef = useRef(null);
  const [stats, setStats] = useState({
    activeJobs: 0,
    completedJobs: 0,
    status: 'idle',
  });

  useEffect(() => {
    fetchData();
    return () => {
      // Cleanup interval on unmount
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, activeRes, completedRes] = await Promise.all([
        api.get('/drivers/profile/'),
        api.get('/drivers/jobs_active/'),
        api.get('/drivers/jobs_completed/'),
      ]);

      setStats({
        activeJobs: activeRes.data.length,
        completedJobs: completedRes.data.length,
        status: profileRes.data.status,
      });

      // Set current job if available
      if (profileRes.data.current_job_info) {
        setCurrentJob(profileRes.data.current_job_info);
      } else {
        setCurrentJob(null);
      }

      // Check if driver has a vehicle assigned
      const hasVehicleAssigned = !!profileRes.data.current_vehicle;
      setHasVehicle(hasVehicleAssigned);
      
      // Fetch vehicle details if assigned
      if (hasVehicleAssigned) {
        fetchVehicle();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicle = async () => {
    setLoadingVehicle(true);
    try {
      // Get vehicle ID from profile
      const profileRes = await api.get('/drivers/profile/');
      const vehicleId = profileRes.data.current_vehicle || profileRes.data.current_vehicle_info?.id;
      
      if (vehicleId) {
        try {
          // Fetch full vehicle details
          const vehicleRes = await api.get(`/vehicles/${vehicleId}/`);
          setVehicle(vehicleRes.data);
          setVehicleStatus(vehicleRes.data.status || 'idle');
          setDieselQuantity(vehicleRes.data.diesel_quantity || '');
          setDefQuantity(vehicleRes.data.def_quantity || '');
          setHasVehicle(true);
        } catch (vehicleError) {
          // If vehicle not found (404), it might have been deleted or unassigned
          if (vehicleError.response?.status === 404) {
            console.warn(`Vehicle ${vehicleId} not found - may have been deleted or unassigned`);
            setVehicleError('Vehicle not found. It may have been deleted or unassigned.');
            setHasVehicle(false);
          } else {
            throw vehicleError; // Re-throw other errors
          }
        }
      } else {
        setHasVehicle(false);
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      if (error.response?.status !== 404) {
        setVehicleError('Failed to load vehicle information');
      }
      setHasVehicle(false);
    } finally {
      setLoadingVehicle(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser. Please use a modern browser with GPS support.'));
        return;
      }

      // Check if we're in a secure context (HTTPS required for geolocation on mobile)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        reject(new Error('GPS location requires HTTPS. Please access the app via a secure connection.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Verify we got valid coordinates
          if (position.coords.latitude == null || position.coords.longitude == null) {
            reject(new Error('Invalid GPS coordinates received. Please ensure location services are enabled.'));
            return;
          }
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          // Provide user-friendly error messages based on error code
          let errorMessage = 'Failed to get GPS location. ';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Location permission denied. Please enable location access in your browser settings and allow location access for this site.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'GPS position unavailable. Please ensure location services are enabled on your device and you have a clear view of the sky.';
              break;
            case error.TIMEOUT:
              errorMessage += 'GPS request timed out. Please try again. Make sure you are outdoors or near a window for better GPS signal.';
              break;
            default:
              errorMessage += `Error: ${error.message || 'Unknown error'}. Please check your device GPS settings.`;
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true, // Use GPS on mobile devices
          timeout: 15000, // Increased timeout for mobile GPS (15 seconds)
          maximumAge: 10000, // Accept cached position up to 10 seconds old (mobile-friendly)
        }
      );
    });
  };

  const updateLocation = async () => {
    if (!hasVehicle) {
      setLocationError('No vehicle assigned. Please assign a vehicle first.');
      return;
    }

    setUpdatingLocation(true);
    setLocationError('');
    setLocationSuccess('');

    try {
      const location = await getCurrentLocation();
      setCurrentLocation(location);

      const response = await api.post('/drivers/update_location/', {
        latitude: location.latitude,
        longitude: location.longitude,
      });

      setLocationSuccess('Location updated successfully!');
      setLocationError('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setLocationSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating location:', error);
      if (error.message) {
        setLocationError(error.message);
      } else if (error.response?.data?.error) {
        setLocationError(error.response.data.error);
      } else if (error.response?.data?.detail) {
        setLocationError(error.response.data.detail);
      } else {
        setLocationError('Failed to update location. Please try again.');
      }
    } finally {
      setUpdatingLocation(false);
    }
  };

  const startLocationTracking = () => {
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
    }

    // Update immediately
    updateLocation();

    // Then update every 10 seconds
    locationIntervalRef.current = setInterval(() => {
      updateLocation();
    }, 10000); // 10 seconds

    // Only show success message if manually triggered (not auto-started)
    if (!locationIntervalRef.current || locationIntervalRef.current._autoStarted) {
      // Silent auto-start
    } else {
      setLocationSuccess('Location tracking started. Your location will be updated every 10 seconds.');
    }
  };

  const stopLocationTracking = () => {
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
      setLocationSuccess('Location tracking stopped.');
    }
  };

  // Automatically start location tracking when vehicle is assigned
  useEffect(() => {
    if (hasVehicle && !locationIntervalRef.current) {
      // Start tracking automatically
      // Update immediately
      updateLocation();

      // Then update every 10 seconds
      locationIntervalRef.current = setInterval(() => {
        updateLocation();
      }, 10000); // 10 seconds
    } else if (!hasVehicle && locationIntervalRef.current) {
      // Stop tracking if vehicle is unassigned
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
      setLocationSuccess('Location tracking stopped.');
    }
    
    // Cleanup on unmount or when hasVehicle changes
    return () => {
      if (!hasVehicle && locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVehicle]);

  const getCurrentLocationForCompletion = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser. Please use a modern browser with GPS support.'));
        return;
      }

      // Check if we're in a secure context (HTTPS required for geolocation on mobile)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        reject(new Error('GPS location requires HTTPS. Please access the app via a secure connection.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Verify we got valid coordinates
          if (position.coords.latitude == null || position.coords.longitude == null) {
            reject(new Error('Invalid GPS coordinates received. Please ensure location services are enabled.'));
            return;
          }
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          // Provide user-friendly error messages based on error code
          let errorMessage = 'Failed to get GPS location. ';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Location permission denied. Please enable location access in your browser settings and allow location access for this site.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'GPS position unavailable. Please ensure location services are enabled on your device and you have a clear view of the sky.';
              break;
            case error.TIMEOUT:
              errorMessage += 'GPS request timed out. Please try again. Make sure you are outdoors or near a window for better GPS signal.';
              break;
            default:
              errorMessage += `Error: ${error.message || 'Unknown error'}. Please check your device GPS settings.`;
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true, // Use GPS on mobile devices
          timeout: 15000, // Increased timeout for mobile GPS (15 seconds)
          maximumAge: 10000, // Accept cached position up to 10 seconds old (mobile-friendly)
        }
      );
    });
  };

  const handleOpenCompleteDialog = async () => {
    if (!currentJob) return;

    setDeliveryPhotos([]);
    setCompletionFormData({
      diesel_delivered: currentJob.diesel_quantity || '',
      def_delivered: currentJob.def_quantity || '',
      completion_notes: '',
    });
    setCompletionError('');
    setCompleteDialogOpen(true);
    
    // Get current GPS location
    setGettingLocation(true);
    try {
      const location = await getCurrentLocationForCompletion();
      setCompletionFormData(prev => ({
        ...prev,
        latitude: location.latitude,
        longitude: location.longitude,
      }));
    } catch (error) {
      console.error('Error getting location:', error);
      // Continue without location
    } finally {
      setGettingLocation(false);
    }
  };

  const handleCloseCompleteDialog = () => {
    setDeliveryPhotos((prev) => {
      prev.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
      return [];
    });
    setCompleteDialogOpen(false);
    setCompletionFormData({
      diesel_delivered: '',
      def_delivered: '',
      completion_notes: '',
    });
    setSubJobs([]);
    setCompletionError('');
  };
  
  const handleAddSubJob = () => {
    // Pre-populate with current job's address if available
    const defaultAddress = currentJob ? {
      delivery_address: currentJob.delivery_address || currentJob.job_location?.split(',')[0] || '',
      delivery_city: currentJob.delivery_city || '',
      delivery_state: currentJob.delivery_state || '',
      delivery_zip: currentJob.delivery_zip || '',
    } : {
      delivery_address: '',
      delivery_city: '',
      delivery_state: '',
      delivery_zip: '',
    };
    
    setSubJobs([...subJobs, {
      ...defaultAddress,
      delivery_notes: '',
      diesel_delivered: '',
      def_delivered: '',
      latitude: null,
      longitude: null,
    }]);
  };
  
  const handleRemoveSubJob = (index) => {
    setSubJobs(subJobs.filter((_, i) => i !== index));
  };
  
  const handleSubJobChange = (index, field, value) => {
    const updated = [...subJobs];
    updated[index] = { ...updated[index], [field]: value };
    setSubJobs(updated);
  };
  
  const handleGetLocationForSubJob = async (index) => {
    try {
      const location = await getCurrentLocationForCompletion();
      handleSubJobChange(index, 'latitude', location.latitude);
      handleSubJobChange(index, 'longitude', location.longitude);
    } catch (error) {
      console.error('Error getting location for sub-job:', error);
    }
  };

  const handleCompletionFormChange = (e) => {
    setCompletionFormData({
      ...completionFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDeliveryPhotoInput = async (e) => {
    // Copy File objects immediately — clearing the input or awaiting GPS empties `e.target.files` in many browsers.
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    e.target.value = '';
    setCompletionError('');

    let plat = completionFormData.latitude;
    let plng = completionFormData.longitude;
    if (plat == null || plng == null) {
      try {
        const loc = await getCurrentLocationForCompletion();
        plat = loc.latitude;
        plng = loc.longitude;
        setCompletionFormData((p) => ({ ...p, latitude: plat, longitude: plng }));
      } catch (err) {
        console.warn('Could not get GPS for delivery photos:', err);
      }
    }

    setDeliveryPhotos((prev) => {
      const remaining = 10 - prev.length;
      if (remaining <= 0) {
        setCompletionError('Maximum of 10 delivery photos.');
        return prev;
      }
      const slice = selectedFiles.slice(0, remaining);
      if (selectedFiles.length > remaining) {
        setCompletionError(`Only ${remaining} more photo(s) allowed (10 maximum).`);
      }
      const next = [...prev];
      slice.forEach((file) => {
        next.push({
          id: `${Date.now()}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
          latitude: plat ?? null,
          longitude: plng ?? null,
        });
      });
      return next;
    });
  };

  const removeDeliveryPhoto = (id) => {
    setDeliveryPhotos((prev) => {
      const row = prev.find((p) => p.id === id);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleCompleteJob = async () => {
    if (!currentJob) {
      setCompletionError('No job selected');
      return;
    }

    setCompletionError('');

    // Validate required fields
    if (!completionFormData.diesel_delivered && !completionFormData.def_delivered) {
      setCompletionError('Please enter at least one quantity (diesel or DEF)');
      return;
    }

    try {
      // Get driver profile first to get driver ID
      const profileRes = await api.get('/drivers/profile/');
      const driverId = profileRes.data?.id;
      
      if (!driverId) {
        setCompletionError('Driver profile not found');
        return;
      }

      const subJobsPayload =
        subJobs.length > 0
          ? subJobs.map((subJob) => ({
              delivery_address: subJob.delivery_address || '',
              delivery_city: subJob.delivery_city || '',
              delivery_state: subJob.delivery_state || '',
              delivery_zip: subJob.delivery_zip || '',
              delivery_notes: subJob.delivery_notes || '',
              diesel_delivered: subJob.diesel_delivered ? parseFloat(subJob.diesel_delivered) : 0,
              def_delivered: subJob.def_delivered ? parseFloat(subJob.def_delivered) : 0,
              latitude: subJob.latitude || null,
              longitude: subJob.longitude || null,
            }))
          : null;

      const formData = buildCompleteJobFormData({
        jobId: currentJob.id,
        dieselDelivered: completionFormData.diesel_delivered
          ? parseFloat(completionFormData.diesel_delivered)
          : 0,
        defDelivered: completionFormData.def_delivered ? parseFloat(completionFormData.def_delivered) : 0,
        completionNotes: completionFormData.completion_notes || '',
        latitude: completionFormData.latitude,
        longitude: completionFormData.longitude,
        subJobsPayload,
        deliveryPhotos,
      });

      const response = await api.post(`/drivers/${driverId}/complete_job/`, formData);
      
      // Check if sub-jobs were created
      if (response.data.sub_jobs_created !== undefined) {
        const subJobsMsg = response.data.sub_jobs_created > 0 
          ? ` with ${response.data.sub_jobs_created} sub-delivery stop${response.data.sub_jobs_created > 1 ? 's' : ''}`
          : '';
        alert(`Job marked as complete successfully${subJobsMsg}!`);
        
        // Show warnings if any sub-jobs failed
        if (response.data.sub_jobs_errors && response.data.sub_jobs_errors.length > 0) {
          console.warn('Some sub-jobs failed to create:', response.data.sub_jobs_errors);
          alert(`Warning: Some sub-deliveries failed to save. Please check the job details.`);
        }
      } else {
        alert('Job marked as complete successfully!');
      }
      
      handleCloseCompleteDialog();
      // Refresh dashboard data
      await fetchData();
    } catch (error) {
      console.error('Error completing job:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error completing job. Please try again.';
      setCompletionError(errorMsg);
    }
  };

  const handleRejectJob = async () => {
    if (!currentJob) {
      setRejectError('No job selected');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this job? This will unassign you from the job.')) {
      return;
    }

    setRejectingJob(true);
    setRejectError('');

    try {
      // Get driver profile first to get driver ID
      const profileRes = await api.get('/drivers/profile/');
      const driverId = profileRes.data?.id;
      
      if (!driverId) {
        setRejectError('Driver profile not found');
        return;
      }
      
      await api.post(`/drivers/${driverId}/reject_job/`, { job_id: currentJob.id });
      alert('Job rejected successfully');
      // Refresh dashboard data
      await fetchData();
    } catch (error) {
      console.error('Error rejecting job:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error rejecting job. Please try again.';
      setRejectError(errorMsg);
      alert(errorMsg);
    } finally {
      setRejectingJob(false);
    }
  };

  const handleVehicleUpdate = async () => {
    setUpdatingVehicle(true);
    setVehicleError('');
    setVehicleSuccess('');

    const payload = {};
    if (vehicleStatus !== vehicle?.status) {
      payload.status = vehicleStatus;
    }
    if (dieselQuantity !== '' && parseFloat(dieselQuantity) !== parseFloat(vehicle?.diesel_quantity || 0)) {
      payload.diesel_quantity = parseFloat(dieselQuantity);
    }
    if (defQuantity !== '' && parseFloat(defQuantity) !== parseFloat(vehicle?.def_quantity || 0)) {
      payload.def_quantity = parseFloat(defQuantity);
    }

    if (Object.keys(payload).length === 0) {
      setVehicleError('No changes to save');
      setUpdatingVehicle(false);
      return;
    }

    try {
      const response = await api.put('/drivers/update_vehicle_status/', payload);
      setVehicle(response.data.vehicle);
      setVehicleStatus(response.data.vehicle.status || 'idle');
      setDieselQuantity(response.data.vehicle.diesel_quantity || '');
      setDefQuantity(response.data.vehicle.def_quantity || '');
      setVehicleSuccess('Vehicle updated successfully!');
      setTimeout(() => setVehicleSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.detail || 
                      'Failed to update vehicle. Please try again.';
      setVehicleError(errorMsg);
    } finally {
      setUpdatingVehicle(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Driver Dashboard
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Jobs
                </Typography>
                <Typography variant="h4">{stats.activeJobs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed Jobs
                </Typography>
                <Typography variant="h4">{stats.completedJobs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Status
                </Typography>
                <Typography variant="h4" textTransform="capitalize">
                  {stats.status ? stats.status.replace('_', ' ') : 'Unknown'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Current Job Panel */}
        {currentJob && (
          <Card sx={{ mt: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon />
                  Current Job
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Job ID</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {currentJob.id ? currentJob.id.substring(0, 8) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Location</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {(() => {
                      const line = formatJobDeliveryLine(currentJob);
                      return line ? (
                        <Link href={googleMapsQueryUrl(line)} target="_blank" rel="noopener noreferrer">
                          {line}
                        </Link>
                      ) : (
                        'N/A'
                      );
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Diesel Quantity</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {currentJob.diesel_quantity ? `${currentJob.diesel_quantity} gal` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">DEF Quantity</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {currentJob.def_quantity ? `${currentJob.def_quantity} gal` : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    {/* Show reject button for accepted or cancelled jobs */}
                    {(currentJob.job_status === 'accepted' || currentJob.job_status === 'cancelled') && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={handleRejectJob}
                        disabled={rejectingJob}
                      >
                        {rejectingJob ? 'Rejecting...' : 'Reject Job'}
                      </Button>
                    )}
                    {/* Show complete button only for accepted or active jobs (not cancelled) */}
                    {(currentJob.job_status === 'accepted' || currentJob.job_status === 'active') && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={handleOpenCompleteDialog}
                      >
                        Mark Complete
                      </Button>
                    )}
                  </Box>
                  {rejectError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setRejectError('')}>
                      {rejectError}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Complete Job Dialog */}
        <Dialog 
          open={completeDialogOpen} 
          onClose={handleCloseCompleteDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Complete Job</DialogTitle>
          <DialogContent>
            {completionError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCompletionError('')}>
                {completionError}
              </Alert>
            )}

            {currentJob && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Job: {currentJob.id?.substring(0, 8)}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  Location:{' '}
                  {(() => {
                    const line = formatJobDeliveryLine(currentJob);
                    return line ? (
                      <Link href={googleMapsQueryUrl(line)} target="_blank" rel="noopener noreferrer">
                        {line}
                      </Link>
                    ) : (
                      'N/A'
                    );
                  })()}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Delivered Quantities
              </Typography>
              
              <TextField
                name="diesel_delivered"
                label="Diesel delivered (gallons)"
                type="number"
                value={completionFormData.diesel_delivered}
                onChange={handleCompletionFormChange}
                fullWidth
                inputProps={{ 
                  step: '0.01',
                  min: 0
                }}
                helperText="Diesel delivered, in gallons"
              />

              <TextField
                name="def_delivered"
                label="DEF delivered (gallons)"
                type="number"
                value={completionFormData.def_delivered}
                onChange={handleCompletionFormChange}
                fullWidth
                inputProps={{ 
                  step: '0.01',
                  min: 0
                }}
                helperText="DEF delivered, in gallons"
              />

              <TextField
                name="completion_notes"
                label="Completion Notes"
                multiline
                rows={3}
                value={completionFormData.completion_notes}
                onChange={handleCompletionFormChange}
                fullWidth
                helperText="Any additional notes about the delivery"
              />

              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Delivery photos (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Up to 10 images (JPEG, PNG, or WebP). Each photo is tagged with GPS from this device when
                  available; otherwise coordinates may be read from the photo (EXIF) or from the completion
                  location above.
                </Typography>
                {/* Overlay file input so the real click hits the input (works in MUI Dialog + Safari; avoids capture+multiple issues on desktop). */}
                <Box sx={{ position: 'relative', display: 'inline-block', mt: 0.5 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<AddPhotoAlternateIcon />}
                    tabIndex={-1}
                    aria-hidden
                    sx={{ pointerEvents: 'none' }}
                    disabled={deliveryPhotos.length >= 10}
                  >
                    Add photos ({deliveryPhotos.length}/10)
                  </Button>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/*"
                    capture="environment"
                    multiple
                    disabled={deliveryPhotos.length >= 10}
                    aria-label="Add delivery photos"
                    onChange={handleDeliveryPhotoInput}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: deliveryPhotos.length >= 10 ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      zIndex: 2,
                    }}
                  />
                </Box>
                {deliveryPhotos.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    {deliveryPhotos.map((p) => (
                      <Box key={p.id} sx={{ position: 'relative', width: 88, height: 88 }}>
                        <Box
                          component="img"
                          src={p.previewUrl}
                          alt=""
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeDeliveryPhoto(p.id)}
                          sx={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            p: 0.25,
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {gettingLocation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Getting GPS location...
                  </Typography>
                </Box>
              )}

              {completionFormData.latitude != null && completionFormData.longitude != null && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Location captured: {completionFormData.latitude.toFixed(6)}, {completionFormData.longitude.toFixed(6)}
                </Alert>
              )}
            </Box>

            {/* Sub-Jobs Section */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Additional Delivery Stops (Sub-Jobs)
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddSubJob}
                  size="small"
                  variant="outlined"
                >
                  Add Stop
                </Button>
              </Box>

              {subJobs.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No additional stops. Click "Add Stop" to add multiple delivery locations.
                </Typography>
              )}

              {subJobs.map((subJob, index) => (
                <Paper key={index} elevation={2} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Stop {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSubJob(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Delivery Address"
                      value={subJob.delivery_address}
                      onChange={(e) => handleSubJobChange(index, 'delivery_address', e.target.value)}
                      fullWidth
                      size="small"
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="City"
                        value={subJob.delivery_city}
                        onChange={(e) => handleSubJobChange(index, 'delivery_city', e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="State"
                        value={subJob.delivery_state}
                        onChange={(e) => handleSubJobChange(index, 'delivery_state', e.target.value)}
                        size="small"
                        sx={{ width: 100 }}
                        inputProps={{ maxLength: 2 }}
                      />
                      <TextField
                        label="ZIP"
                        value={subJob.delivery_zip}
                        onChange={(e) => handleSubJobChange(index, 'delivery_zip', e.target.value)}
                        size="small"
                        sx={{ width: 120 }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="Diesel delivered (gallons)"
                        type="number"
                        value={subJob.diesel_delivered}
                        onChange={(e) => handleSubJobChange(index, 'diesel_delivered', e.target.value)}
                        fullWidth
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                      />
                      <TextField
                        label="DEF delivered (gallons)"
                        type="number"
                        value={subJob.def_delivered}
                        onChange={(e) => handleSubJobChange(index, 'def_delivered', e.target.value)}
                        fullWidth
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                      />
                    </Box>

                    <TextField
                      label="Delivery Notes"
                      multiline
                      rows={2}
                      value={subJob.delivery_notes}
                      onChange={(e) => handleSubJobChange(index, 'delivery_notes', e.target.value)}
                      fullWidth
                      size="small"
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        startIcon={<LocationIcon />}
                        onClick={() => handleGetLocationForSubJob(index)}
                        size="small"
                        variant="outlined"
                      >
                        Get Location
                      </Button>
                      {subJob.latitude != null && subJob.longitude != null && (
                        <Typography variant="body2" color="text.secondary">
                          {subJob.latitude.toFixed(6)}, {subJob.longitude.toFixed(6)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCompleteDialog}>Cancel</Button>
            <Button 
              onClick={handleCompleteJob} 
              variant="contained" 
              color="success"
              disabled={gettingLocation}
            >
              Mark as Complete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Vehicle Panel */}
        {hasVehicle && (
          <Card sx={{ mt: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CarIcon />
                  Current Vehicle
                </Typography>
              </Box>

              {loadingVehicle ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                  <CircularProgress size={40} />
                </Box>
              ) : vehicle ? (
                <>
                  {vehicleError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setVehicleError('')}>
                      {vehicleError}
                    </Alert>
                  )}

                  {vehicleSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setVehicleSuccess('')}>
                      {vehicleSuccess}
                    </Alert>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">License Plate</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {vehicle.license_plate || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Make & Model</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {vehicle.make} {vehicle.model} {vehicle.year ? `(${vehicle.year})` : ''}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Diesel quantity (gallons)"
                        type="number"
                        value={dieselQuantity}
                        onChange={(e) => setDieselQuantity(e.target.value)}
                        inputProps={{ step: '0.01', min: '0' }}
                        disabled={updatingVehicle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="DEF quantity (gallons)"
                        type="number"
                        value={defQuantity}
                        onChange={(e) => setDefQuantity(e.target.value)}
                        inputProps={{ step: '0.01', min: '0' }}
                        disabled={updatingVehicle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Vehicle Status</InputLabel>
                        <Select
                          value={vehicleStatus}
                          onChange={(e) => setVehicleStatus(e.target.value)}
                          label="Vehicle Status"
                          disabled={updatingVehicle}
                        >
                          <MenuItem value="idle">Idle</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="refueling">Refueling</MenuItem>
                          <MenuItem value="out_of_service">Out of Service</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={handleVehicleUpdate}
                        disabled={updatingVehicle}
                        fullWidth
                      >
                        {updatingVehicle ? 'Updating...' : 'Update Vehicle'}
                      </Button>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Alert severity="info">
                  Vehicle information not available. Please contact a manager.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </Layout>
  );
};

export default DriverDashboard;



